import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Button, Div, Image, Modal, useTheme, type ModalRef } from "react-better-html";
import { useHotkeys } from "react-hotkeys-hook";
import {
   TransformComponent,
   TransformWrapper,
   type ReactZoomPanPinchContentRef,
   type ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";

type ImageViewModalProps = {
   imageSrc?: string;
   onClickNext?: () => void;
   onClickPrevious?: () => void;
} & React.ComponentProps<typeof Modal>;

const wrapperStyle: React.CSSProperties = {
   width: "100%",
};

const contentStyle: React.CSSProperties = {
   width: "100%",
};

const ImageViewModal = forwardRef<ModalRef, ImageViewModalProps>(
   ({ imageSrc, onClickNext, onClickPrevious, ...props }, ref) => {
      const theme = useTheme();

      const modalRef = useRef<ModalRef>(null);
      const transformWrapperRef = useRef<ReactZoomPanPinchContentRef>(null);

      const [zoom, setZoom] = useState<number>(1);

      const onMouseDownTransform = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
         transformWrapperRef.current?.instance.onPanningStart(event.nativeEvent);
      }, []);

      const [imageAspectRatio, setImageAspectRatio] = useState<number>();

      const onZoom = useCallback((ref: ReactZoomPanPinchRef) => {
         setZoom(ref.state.scale);
      }, []);
      const onLoadImage = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
         const { naturalWidth, naturalHeight } = event.currentTarget;

         if (naturalWidth && naturalHeight) setImageAspectRatio(naturalWidth / naturalHeight);
      }, []);
      const onPressKeyboardLeftArrow = useCallback(() => onClickPrevious?.(), [onClickPrevious]);
      const onPressKeyboardRightArrow = useCallback(() => onClickNext?.(), [onClickNext]);

      const wrapperProps = useMemo<React.HTMLAttributes<HTMLDivElement>>(
         () => ({
            onMouseDown: onMouseDownTransform,
         }),
         [onMouseDownTransform],
      );

      useEffect(() => {
         setImageAspectRatio(undefined);
      }, [imageSrc]);

      useHotkeys("left", onPressKeyboardLeftArrow, {
         preventDefault: true,
      });
      useHotkeys("right", onPressKeyboardRightArrow, {
         preventDefault: true,
      });

      useImperativeHandle(ref, (): ModalRef => modalRef.current!, []);

      const imageZoom = theme.styles.borderRadius * 2;
      const imageSideSpace = theme.styles.space;
      const imageContentSideSpace = theme.styles.gap;

      return (
         <Modal
            maxWidth={
               imageAspectRatio
                  ? `min(calc(100vw - ${imageSideSpace * 2}px), calc((100vh - ${imageSideSpace * 2}px) * ${imageAspectRatio}))`
                  : `calc(100vw - ${imageSideSpace * 2}px)`
            }
            backgroundColor={theme.colors.label}
            transition="none"
            padding={0}
            closeButtonLight
            outsideComponentAbove={
               <>
                  <Button.icon
                     position="absolute"
                     top="50%"
                     left={imageSideSpace + imageContentSideSpace}
                     icon="chevronLeft"
                     transform="translateY(-50%)"
                     color={theme.colors.base}
                     backgroundColor={theme.colors.label + "60"}
                     backgroundColorHover={theme.colors.label + "80"}
                     onClick={onClickPrevious}
                  />
                  <Button.icon
                     position="absolute"
                     top="50%"
                     right={imageSideSpace + imageContentSideSpace}
                     icon="chevronRight"
                     transform="translateY(-50%)"
                     color={theme.colors.base}
                     backgroundColor={theme.colors.label + "60"}
                     backgroundColorHover={theme.colors.label + "80"}
                     onClick={onClickNext}
                  />
               </>
            }
            {...props}
            ref={modalRef}
         >
            <TransformWrapper ref={transformWrapperRef} initialScale={1} onZoom={onZoom}>
               {({ zoomIn, zoomOut, resetTransform }) => (
                  <>
                     <Div
                        width="100%"
                        maxHeight={`calc(100vh - ${imageSideSpace * 2}px)`}
                        borderRadius={imageZoom}
                        overflow="hidden"
                     >
                        <TransformComponent
                           wrapperStyle={wrapperStyle}
                           contentStyle={contentStyle}
                           wrapperProps={wrapperProps}
                        >
                           <Image
                              width="100%"
                              height="auto"
                              maxHeight={`calc(100vh - ${imageSideSpace * 2}px)`}
                              objectFit="contain"
                              src={imageSrc}
                              onLoad={onLoadImage}
                              borderRadius={imageZoom / zoom - 1}
                           />
                        </TransformComponent>
                     </Div>

                     <Div.column
                        position="absolute"
                        bottom={imageContentSideSpace}
                        right={imageContentSideSpace}
                        gap={theme.styles.gap / 2}
                     >
                        <Button.icon
                           icon="refresh"
                           color={theme.colors.base}
                           backgroundColor={theme.colors.label + "60"}
                           backgroundColorHover={theme.colors.label + "80"}
                           onClick={() => resetTransform()}
                        />
                        <Button.icon
                           icon="plus"
                           color={theme.colors.base}
                           backgroundColor={theme.colors.label + "60"}
                           backgroundColorHover={theme.colors.label + "80"}
                           onClick={() => zoomIn()}
                        />
                        <Button.icon
                           icon="minus"
                           color={theme.colors.base}
                           backgroundColor={theme.colors.label + "60"}
                           backgroundColorHover={theme.colors.label + "80"}
                           onClick={() => zoomOut()}
                        />
                     </Div.column>
                  </>
               )}
            </TransformWrapper>
         </Modal>
      );
   },
);

export default memo(ImageViewModal);
